import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { map, isEmpty } from "lodash";
import { ParcelOrderList } from "Components/order-management/parcel-order-list";
import { Col, Row, Radio } from "antd";
import { ExclamationCircleOutlined, MoreOutlined } from "@ant-design/icons";
import {
  ActionMenu,
  Button,
  BasicModal,
} from "@life-line-lab/components-react";
import useSession from "Hooks/session.hook";
import ArchiveSettings from "Components/order-management/shared/parcel-modals/archive-settings";
import { useOrderListingStates } from "Helpers/state-providers";
import useCorporateType from "Hooks/corporate-type.hook";

const RadioComponents = (props) => {
  const {
    handleSwitchTabs,
    archiveState,
    setCallModal,
    handleActionClick,
    handleSetupArchiveSettings,
    handleResetModalState,
    archiveSettings,
    isSettingsShown,
  } = props || {};

  const { t } = useTranslation();

  return (
    <div className="radio-buttons">
      <Row gutter={[8, 0]} align="middle">
        <Col>
          <Radio.Group
            defaultValue="latest"
            buttonStyle="solid"
            onChange={(e) => handleSwitchTabs(e.target.value)}
          >
            <Radio.Button value="latest">{t("option.latest")}</Radio.Button>
            <Radio.Button value="archive">{t("option.archive")}</Radio.Button>
          </Radio.Group>
        </Col>
        <Col>
          {isSettingsShown && archiveState && (
            <span className="manage-archive-settings">
              <a
                onClick={() => {
                  setCallModal(
                    <ArchiveSettings
                      onSubmit={handleSetupArchiveSettings}
                      onCancel={handleResetModalState}
                      archiveSettings={archiveSettings}
                    />,
                  );
                  handleActionClick();
                }}
              >
                {t("txt.manageArchiveSettings")}
              </a>
            </span>
          )}
          {!isSettingsShown && !archiveState && (
            <div className="archive-info">
              <ExclamationCircleOutlined />
              <span>{t("txt.orderArchiveDesc")}</span>
            </div>
          )}
        </Col>
      </Row>
    </div>
  );
};

const RejectedOrdersTab = ({
  baseColumns2,
  handleRefreshData,
  allArchiveObjects,
  handleModalAction,
}) => {
  const { org } = useSession();
  const { t } = useTranslation();
  const { selectedRows } = useOrderListingStates();

  const {
    archiveSettingsObject,
    archiveStateObject,
    handleSetArchiveOrders,
    handleFetchArchiveSettings,
    modifySettingsFunctions,
  } = allArchiveObjects || {};

  const { isArchive, setIsArchive, archivedOrdersList } =
    archiveStateObject || {};
  const { archiveSettings } = archiveSettingsObject || {};
  const { handleCreateArchiveSettings } = modifySettingsFunctions || {};

  const { isCustomer, isLogisticProvider, isCompany } = useCorporateType();

  const initialModal: any = {
    open: false,
  };

  const [modal, setModal] = useState<any>(initialModal);
  const [callModal, setCallModal] = useState<any>(<></>);

  const handleResetModalState = () => {
    setModal(initialModal);
  };

  const handleActionClick = () => {
    setModal({ open: true });
  };

  const handleSwitchTabs = (value: string) => {
    setIsArchive(value === "archive");
    isCompany && value === "archive" && handleFetchArchiveSettings(org._id);
    handleRefreshData();
  };

  const handleSetupArchiveSettings = (data: any) => {
    let archiveSettingInput: any = {
      archiveSettingID: archiveSettings?._id,
      noOfOrders: data?.noOfItems,
      frequency: data?.frequency,
    };

    if (isEmpty(archiveSettings)) {
      archiveSettingInput = {
        orgID: org._id,
        orgName: "DIOLKO",
        startDate: new Date(),
        noOfOrders: data.noOfItems,
        frequency: data.frequency,
        type: "ORDER",
      };
    }
    handleCreateArchiveSettings(archiveSettingInput);

    setModal({ open: false });
  };

  const customerColumns = baseColumns2.concat([
    {
      title: t("txt.receiver").toLocaleUpperCase(),
      dataIndex: ["receiverInfo", "fullName"],
      width: 100,
      ellipsis: true,
    },
    {
      title: t("label.orderDate").toLocaleUpperCase(),
      dataIndex: "createdAt",
      width: 105,
      ellipsis: true,
      sorter: (a, b) =>
        a.orderDate > b.orderDate ? 1 : a.orderDate < b.orderDate ? -1 : 0,
      render: (createdAt: any) => {
        return <span>{moment(createdAt).format("DD-MM-YYYY") || null}</span>;
      },
    },
    {
      title: t("label.issueType").toLocaleUpperCase(),
      dataIndex: ["orderStatusInfo"],
      width: 150,
      render: (orderStatusInfo: any) => {
        return (
          <span>
            {map(orderStatusInfo?.issueType, (issue) => {
              return (
                <span>
                  {issue?.name || <span>&#11835;</span>}
                  <br />
                </span>
              );
            })}
          </span>
        );
      },
    },
    {
      title: t("label.remarks").toLocaleUpperCase(),
      dataIndex: ["orderStatusInfo"],
      width: 80,
      render: (orderStatusInfo: any) => {
        return (
          <span>
            {map(orderStatusInfo?.issueType, (issue) => {
              return (
                <span>
                  {issue?.remarks || <span>&#11835;</span>}
                  <br />
                </span>
              );
            })}
          </span>
        );
      },
    },
  ]);

  const companyColumns = customerColumns.concat({
    title: "",
    dataIndex: [],
    width: 80,
    render: (_: any, record: any) => (
      <ActionMenu
        label={<MoreOutlined />}
        actions={[
          {
            label: t("action.reviewOrderAgain"),
            onClick: () => {
              const allOrderIDs = record.childrenOrderIDs
                ? [record._id, ...record.childrenOrderIDs]
                : [record._id];
              const status = isArchive ? "REVERT" : "CONFIRM";

              handleModalAction(status, allOrderIDs);
            },
            isDisable: false,
          },
        ]}
      />
    ),
  });

  return (
    <>
      <ParcelOrderList
        columns={isCustomer ? customerColumns : companyColumns}
        additionalComponent={
          <>
            {!isLogisticProvider && !isArchive && (
              <div className="btn-actions-container">
                <Button
                  className="archive"
                  label={t("action.archive")}
                  type="default"
                  size="large"
                  isDisabled={selectedRows?.length === 0}
                  onClick={() => {
                    handleSetArchiveOrders(selectedRows);
                  }}
                />
              </div>
            )}
          </>
        }
        isCheckBoxShown={isArchive || !isLogisticProvider}
        filterItems={[]}
        radioButtonComponent={
          <RadioComponents
            handleSwitchTabs={handleSwitchTabs}
            archiveState={isArchive}
            setCallModal={setCallModal}
            handleActionClick={handleActionClick}
            handleSetupArchiveSettings={handleSetupArchiveSettings}
            handleResetModalState={handleResetModalState}
            archiveSettings={archiveSettings}
            isSettingsShown={!isCustomer && !isLogisticProvider}
          />
        }
        archivedOrdersList={isArchive && archivedOrdersList}
        searchPlaceholder={t("label.searchOrderID")}
        handleRefreshData={handleRefreshData}
      />
      <BasicModal
        className="modal"
        isOpen={modal.open}
        isClosable={true}
        isMaskClosable={false}
        onClose={() => {
          handleResetModalState();
        }}
      >
        {callModal}
      </BasicModal>
    </>
  );
};

export default RejectedOrdersTab;
